import { useIntl } from "react-intl";
import { usePostHog } from "posthog-js/react";
import { Flex, Spinner, useMediaQuery, OnboardingLayout } from "@wayflyer/flyui";
import { IconArrowLeft24Line, IconArrowRight24Line } from "@wayflyer/flyui-icons/24/line";
import { useCustomerAppConfig } from "undercarriage";
import { useMount } from "shared-utils";

import { CreditCheckConsent } from "../../components/credit-check-consent-form";
import { MESSAGES } from "../../components/credit-check-consent-form/message-definitions";
import { IncompleteStepsModal } from "../../components/incomplete-steps-modal";
import { useFundingApplicationNavigation, PAGE_TYPE } from "../../hooks/navigation-next";
import { useApplicationSubmit } from "../../hooks/use-application-submit";
import {
  useCreditScoreConsent,
  useCreditScoreConsentMutation,
  useUpdateCreditScoreConsentMutation,
} from "../../queries/credit-score-consent/queries";

const FORM_ID = "credit-check-consent-form";

export const CreditCheckConsentPage = () => {
  const intl = useIntl();
  const posthog = usePostHog();
  const query = useMediaQuery();
  const isMobile = query("sm").windowLte;
  const {
    data: { customerDomain, countryOfIncorporation },
  } = useCustomerAppConfig();
  const navigationContext = useFundingApplicationNavigation({
    currentPage: PAGE_TYPE.CREDIT_CHECK_CONSENT,
  });
  const applicationSubmit = useApplicationSubmit({
    currentPage: PAGE_TYPE.CREDIT_CHECK_CONSENT,
  });
  const { isFetched: isConsentFetched } = useCreditScoreConsent(customerDomain);
  const { isPending: isCreateMutationRunning } = useCreditScoreConsentMutation(customerDomain);
  const { isPending: isUpdateMutationRunning } = useUpdateCreditScoreConsentMutation(customerDomain);
  const isSubmitting = isCreateMutationRunning || isUpdateMutationRunning || applicationSubmit.isSubmitting;

  useMount(() => {
    // Todo: update has_ssn_field when we support countries that don't require SSN for credit check consent.
    posthog?.capture("onboarding_credit_check_viewed", { has_ssn_field: true });
  });

  if (!isConsentFetched || !countryOfIncorporation) {
    return (
      <Flex justify="center" align="center">
        <Spinner size={32} />
      </Flex>
    );
  }

  const onContinue = () => {
    if (!navigationContext.nextPath) {
      applicationSubmit.submitApplication();
    } else {
      navigationContext.navigate(navigationContext.nextPath);
    }
  };

  const handleBack = () => {
    if (navigationContext.previousPath) {
      navigationContext.navigate(navigationContext.previousPath);
    }
  };

  return (
    <>
      <IncompleteStepsModal {...applicationSubmit} />
      <OnboardingLayout
        variant="wide"
        title={intl.formatMessage(MESSAGES.softCreditCheckTitle)}
        subtitle={intl.formatMessage(MESSAGES.creditInfoText)}
        formId={FORM_ID}
        primaryActionType="submit"
        actions={{
          primary: {
            content: intl.formatMessage(MESSAGES.agreeAndSubmitButton),
            icon: IconArrowRight24Line,
            loading: isSubmitting,
            disabled: isSubmitting,
            analyticsId: "credit-check-consent-agree-button",
          },
          secondary: {
            content: isMobile ? undefined : navigationContext.backButtonLabel,
            icon: IconArrowLeft24Line,
            onAction: handleBack,
            analyticsId: "credit-check-consent-back-button",
          },
        }}
      >
        <CreditCheckConsent formId={FORM_ID} onContinue={onContinue} />
      </OnboardingLayout>
    </>
  );
};
